import { useState, useEffect } from 'react'

/**
 * USE SESSION STORAGE HOOK
 *
 * Custom hook for persisting state to sessionStorage.
 * Unlike localStorage, sessionStorage is scoped per-tab — each browser tab
 * gets its own isolated storage. This prevents cross-tab interference
 * (e.g., selecting a project in one tab won't overwrite another tab's selection).
 *
 * @param {string} key - sessionStorage key
 * @param {any} initialValue - Default value if key doesn't exist
 * @returns {[any, function]} - [value, setValue] like useState
 */
function useSessionStorage(key, initialValue) {
  // Get initial value from sessionStorage or use provided default
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.sessionStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.warn(`Error reading sessionStorage key "${key}":`, error)
      return initialValue
    }
  })

  // Update sessionStorage when value changes
  useEffect(() => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(storedValue))
    } catch (error) {
      console.warn(`Error setting sessionStorage key "${key}":`, error)
    }
  }, [key, storedValue])

  return [storedValue, setStoredValue]
}

export { useSessionStorage }
